import { defineStore, storeToRefs } from 'pinia'
import { computed, markRaw, Ref, ref, shallowRef } from 'vue'
import { type CashAppInstance, type Error, type PaymentMethodOption, type TokenPayload, PaymentMethodType } from '@/types'
import { useMutation } from '@vue/apollo-composable'
import { useScript } from '@unhead/vue'
import { head } from '@/utils/head'
import { sentryCapture } from '@/utils/sentry'
import {
  createSubscription as createSubscriptionMutation,
  changeSubscription as changeSubscriptionMutation,
  postBillingInfoUpdate as postBillingInfoUpdateMutation,
  createNoPaySubscription as createNoPaySubscriptionMutation
} from '@/queries'
import { useErrorsStore } from '@/store/ErrorsStore'
import { useModalStore } from '@/store/ModalStore'
import { useCheckoutStore } from '@/store/CheckoutStore'
import { useSubscriptionStore } from '@/store/SubscriptionStore'
import { useProductStore } from '@/store/ProductStore'
import { useRecaptchaStore } from '@/store/RecaptchaStore'
import { usePriceStore } from '@/store/PriceStore'

import { ApolloError } from '@apollo/client'

declare global {
  interface Window {
    recurly?: any
  }
}

export const useRecurlyStore = defineStore('RecurlyStore', () => {
  const errorsStore = useErrorsStore()
  const checkoutStore = useCheckoutStore()
  const productStore = useProductStore()
  const subscriptionStore = useSubscriptionStore()
  const recaptchaStore = useRecaptchaStore()
  const priceStore = usePriceStore()
  const { productName } = storeToRefs(productStore)
  const { isEligibleForTrial } = storeToRefs(subscriptionStore)
  const { billingInfo, checkout, hasValidPaymentDetails, paymentRequired, firstName, lastName, country, currency, postalCode } = storeToRefs(checkoutStore)

  const { setCheckoutProperty, set3DSToken } = checkoutStore
  const { push: pushError, clear: clearErrors } = errorsStore
  const { sendRecaptcha } = recaptchaStore
  const paymentMethod = ref(PaymentMethodType.creditCard)

  // The configured recurly.js global. Captured once via initRecurly() (called when the script
  // finishes loading) so token methods read a stored instance instead of reaching for window.recurly
  // on every call. window.recurly is still where Recurly attaches itself — we just stop touching it
  // directly past the single capture point.
  const recurlyInstance = shallowRef<Window['recurly'] | null>(null)
  const isRecurlyReady = computed(() => recurlyInstance.value !== null)
  const recurlyRequested = ref(false)

  const recurlyElements = shallowRef(null)
  const cashAppInstance = shallowRef<CashAppInstance | null>(null)
  const cashAppIsLoading = ref(false)
  const cashAppContainerSelector = ref<string | null>(null)
  // Set by callers that need to attach a token listener BEFORE start() runs — e.g. the mobile
  // redirect-resume path, where Pay Kit auto-fires the token event during start() as it reads
  // cash_request_id from the URL. If we wait until after start() to call .on('token', ...), the
  // event has already fired and the token never lands in our store. Consumed and cleared on next
  // initCashApp call.
  const pendingCashAppTokenListener = ref<((payload: TokenPayload | null) => void) | null>(null)
  const isCashAppEligible = computed(() => currency.value === 'USD' && country.value === 'US')

  const token: Ref<string | null> = ref(null)

  const { mutate: postCreateSubscription, onDone: onCreateSubscription, onError: onCreateSubscriptionError } = useMutation(createSubscriptionMutation)
  const { mutate: postChangeSubscription, onDone: onChangeSubscription, onError: onChangeSubscriptionError } = useMutation(changeSubscriptionMutation)
  const { mutate: postBillingInfoUpdate, onDone: onBillingInfoUpdate, onError: onBillingInfoUpdateError } = useMutation(postBillingInfoUpdateMutation)

  const isBillingInfoUpdated = computed(() => Object.keys(billingInfo.value).length > 0)

  const availablePaymentMethods = computed<PaymentMethodOption[]>(() => {
    return [
      { id: 'cc', hotkey: 'a', title: 'Credit/Debit Card', value: PaymentMethodType.creditCard },
      { id: 'paypal', hotkey: 'b', title: 'PayPal', value: PaymentMethodType.payPal },
      ...(isCashAppEligible.value ? [{ id: 'cashapp', hotkey: 'c', title: 'Cash App Pay', value: PaymentMethodType.cashApp }] : [])
    ]
  })

  const {
    mutate: postCreateNoPaySubscription,
    onDone: onCreateNoPaySubscription,
    onError: onCreateNoPaySubscriptionError
  } = useMutation(createNoPaySubscriptionMutation)

  // Load recurly.js once and capture/configure it on load. Triggered from a checkout navigation
  // guard (see router) so the instance is ready well before the payment screen renders. The shared
  // head is passed explicitly because useScript() otherwise relies on injectHead(), which only
  // resolves inside component setup — and guards run outside it.
  const loadRecurly = () => {
    if (recurlyRequested.value) return
    recurlyRequested.value = true

    // crossorigin/referrerpolicy are explicitly disabled: unhead defaults cross-origin scripts to
    // crossorigin="anonymous" + referrerpolicy="no-referrer", which forces a CORS-checked fetch.
    // Recurly's CDN serves recurly.js as a plain classic script with no CORS headers, so that fetch
    // is rejected and the script fails to load. Disabling them makes it load like the original
    // index.html <script> tag, which carried neither attribute.
    const script = useScript({ src: 'https://js.recurly.com/v4/recurly.js', crossorigin: false, referrerpolicy: false }, { head })
    script.onLoaded(() => initRecurly())
    script.onError(() => sentryCapture(new Error('Failed to load recurly.js')))
  }

  // Capture and configure the recurly.js global once it has loaded. Configuring here (rather than via
  // an inline <script> injected at build time) keeps the public key alongside the rest of our env
  // usage and removes a build-config hack.
  const initRecurly = (instance: Window['recurly'] = window.recurly) => {
    if (!instance) return
    try {
      instance.configure({
        publicKey: import.meta.env.VITE_OUTPUT_RECURLY_API_PUBLIC_KEY,
        required: ['cvv']
      })
      recurlyInstance.value = markRaw(instance)
      // Create the Elements container as soon as recurly.js is ready. recurly.js now loads
      // asynchronously via useScript, so a component that mounts before it finishes (e.g. the
      // resub payment-details modal, which is always in the DOM) would call instantiateElements()
      // too early, get nothing, and — since onMounted fires once — never render its card fields.
      // Instantiating here restores the pre-async behaviour (elements ready the moment recurly
      // loads). Idempotent: instantiateElements() no-ops if elements already exist. WEB-7719.
      instantiateElements()
    } catch (err) {
      sentryCapture(err as Error)
    }
  }

  const instantiateElements = () => {
    if (!recurlyElements.value) {
      const elements = recurlyInstance.value?.Elements()
      recurlyElements.value = elements ? markRaw(elements) : null
    }
  }

  const setError = (error: Error) => {
    pushError(error)
  }

  const handleError = (err: ApolloError) => {
    sentryCapture(err)
  }

  onCreateSubscriptionError(err => handleError(err))
  onCreateNoPaySubscriptionError(err => handleError(err))

  onChangeSubscriptionError(err => {
    sentryCapture(err)
  })

  onBillingInfoUpdateError(err => {
    sentryCapture(err)
  })

  const setPaymentMethod = (method: string) => (paymentMethod.value = method as PaymentMethodType)

  const getTokenCC = async () => {
    const billingInfo = {
      first_name: firstName.value,
      last_name: lastName.value,
      country: country.value,
      postal_code: postalCode.value
    }

    const recurly = recurlyInstance.value
    if (!recurly || !recurlyElements.value) {
      return false
    }

    return new Promise<boolean>(resolve => {
      recurly.token(recurlyElements.value, billingInfo, (error: Error, tokenPayload: TokenPayload) => {
        if (error) {
          error.code = `recurly_token_${error.code}`
          setError(error)
          resolve(false)
        }

        if (tokenPayload) {
          const { id: tokenId, type, ...recurlyBilling } = tokenPayload

          if (recurlyBilling.card) {
            const { brand: cardType, last_four: lastFour, exp_month: expMonth, exp_year: expYear } = recurlyBilling.card

            setCheckoutProperty({
              paymentDetails: {
                cardType,
                lastFour,
                expMonth,
                expYear,
                type
              }
            })
          }

          setPaymentMethod(type)
          setTokenValue(tokenId)
          resolve(true)
        } else {
          // In case payload is falsy but there's no error
          resolve(false)
        }
      })
    })
  }

  const handleTransactionError = () => {
    token.value = null

    setCheckoutProperty({
      paymentDetails: null
    })
  }

  const getTokenPaypal = async () => {
    const recurly = recurlyInstance.value
    if (!recurly) {
      return false
    }

    const recurlyPaypal = recurly.PayPal({
      display: { displayName: productName.value }
    })

    if (!recurlyPaypal) {
      console.error('PayPal not initialized. Please use a different payment method.')
      return false
    }

    return new Promise<boolean>(resolve => {
      recurlyPaypal
        .on('error', (error: Error) => {
          error.code = `recurly_token_${error.code}`
          setError(error)
          resolve(false)
        })
        .on('cancel', () => {
          resolve(false)
        })
        .on('token', (tokenPayload: TokenPayload) => {
          if (tokenPayload) {
            const { id: tokenId, type } = tokenPayload

            setPaymentMethod(type)
            setTokenValue(tokenId)

            setCheckoutProperty({
              paymentDetails: {
                type
              }
            })

            resolve(true)
          } else {
            resolve(false)
          }
        })
        .start()
    })
  }

  const initCashApp = (containerSelector: string) => {
    const recurly = recurlyInstance.value
    if (!recurly) return

    cashAppContainerSelector.value = containerSelector
    cashAppIsLoading.value = true
    cashAppInstance.value = markRaw(
      recurly.AlternativePaymentMethods({
        allowedPaymentMethods: ['cashapp'],
        containerSelector,
        amount: priceStore.selectedPlanCouponAppliedPrice,
        currency: currency.value,
        countryCode: country.value,
        adyen: {
          publicKey: import.meta.env.VITE_ADYEN_CLIENT_KEY,
          env: import.meta.env.VITE_OUTPUT_ENVIRONMENT === 'production' ? 'live' : 'test'
        }
      })
    )

    // Attach any pre-registered token listener BEFORE start() runs. Required on the mobile
    // redirect-resume path so we don't miss the token event that fires during start().
    if (pendingCashAppTokenListener.value) {
      const listener = pendingCashAppTokenListener.value
      pendingCashAppTokenListener.value = null
      cashAppInstance.value?.on('token', listener)
    }

    // start() resolves once the Adyen widget is mounted. The SDK's 'error' event handler is attached at
    // submit time, so init-time failures (e.g. Adyen library fails to load) would otherwise vanish.
    Promise.resolve(cashAppInstance.value?.start())
      .catch(sentryCapture)
      .finally(() => {
        cashAppIsLoading.value = false
      })
  }

  const refreshCashApp = (containerSelector: string) => {
    // Cash App APM captures `amount` at init time, so a price change requires teardown + rebuild.
    // Bail if the container has already left the DOM (e.g. the screen unmounted mid-update).
    if (!document.querySelector(containerSelector)) return
    destroyCashApp()
    initCashApp(containerSelector)
  }

  const getTokenCashApp = (): Promise<boolean> => {
    const instance = cashAppInstance.value
    if (!instance) return Promise.resolve(false)

    // The Adyen widget enters a terminal state after cancel or error and stacks listeners on each
    // .on() call, so any non-success outcome must rebuild it before another submit attempt.
    const selector = cashAppContainerSelector.value

    return new Promise<boolean>(resolve => {
      instance
        .on('error', (error: Error) => {
          error.code = `recurly_token_${error.code}`
          setError(error)
          if (selector) refreshCashApp(selector)
          resolve(false)
        })
        .on('cancel', () => {
          if (selector) refreshCashApp(selector)
          resolve(false)
        })
        .on('token', (tokenPayload: TokenPayload) => {
          if (tokenPayload) {
            const { id: tokenId } = tokenPayload
            // Recurly returns `cash_app` as the token type, but our PaymentMethodType enum and the
            // downstream display map both key on `cashapp`. Normalize here so the rest of the app
            // sees a single canonical value.
            setPaymentMethod(PaymentMethodType.cashApp)
            setTokenValue(tokenId)
            setCheckoutProperty({ paymentDetails: { type: PaymentMethodType.cashApp } })
            resolve(true)
          } else {
            resolve(false)
          }
        })

      instance.submit()
    })
  }

  const destroyCashApp = () => {
    cashAppInstance.value?.destroy()
    cashAppInstance.value = null
  }

  // Pre-register a one-shot token listener for the next initCashApp call. Used by the mobile
  // redirect-resume path so the listener is attached before start() fires its auto-token event.
  // Returns a promise that resolves with the captured tokenId, or null if the listener never fired.
  const registerCashAppRedirectListener = (): Promise<string | null> => {
    return new Promise<string | null>(resolve => {
      pendingCashAppTokenListener.value = (tokenPayload: TokenPayload | null) => {
        const tokenId = tokenPayload?.id ?? null
        if (tokenId) {
          setPaymentMethod(PaymentMethodType.cashApp)
          setTokenValue(tokenId)
          setCheckoutProperty({ paymentDetails: { type: PaymentMethodType.cashApp } })
        }
        resolve(tokenId)
      }
    })
  }

  const setTokenValue = (value: string) => {
    setCheckoutProperty({
      billingInfo: { tokenId: value },
      paymentRequired: paymentRequired.value
    })
  }

  const getToken = async (): Promise<boolean> => {
    if (!paymentRequired.value) {
      setTokenValue('not_required')
      return true
    }

    switch (paymentMethod.value) {
      case PaymentMethodType.creditCard:
        return await getTokenCC()
      case PaymentMethodType.payPal:
        return await getTokenPaypal()
      case PaymentMethodType.cashApp:
        return await getTokenCashApp()
      default:
        return false
    }
  }

  const submitSubscriptionForm = async (): Promise<boolean> => {
    clearErrors()
    await sendRecaptcha()

    if (hasValidPaymentDetails.value) {
      submitCreateSubscription()
      return true
    }

    const tokenized = await getToken()
    if (tokenized) {
      submitCreateSubscription()
      return true
    }
    return false
  }

  const submitCreateSubscription = () => {
    const payload = {
      ...checkout.value
    }

    if (!paymentRequired.value) {
      postCreateNoPaySubscription({
        trialEndsAt: new Date(0).toISOString(),
        ...payload
      })

      return
    }

    if (!isEligibleForTrial.value) {
      postCreateSubscription({
        trialEndsAt: new Date(0).toISOString(),
        ...payload
      })
      return
    }

    postCreateSubscription({
      ...payload
    })
  }

  const submitChangeSubscription = () => {
    const { billingInfo, ...payload } = checkout.value

    if (isBillingInfoUpdated.value) {
      postBillingInfoUpdate({ ...billingInfo }).then((response: any) => {
        if (!response.data?.setSubscriptionBillingInfo) {
          return
        }

        const {
          data: { setSubscriptionBillingInfo }
        } = response

        if (!setSubscriptionBillingInfo) {
          return
        }

        if (setSubscriptionBillingInfo.error) {
          setError(setSubscriptionBillingInfo.error)
          return
        }

        postChangeSubscription({
          ...payload
        })
      })
    } else {
      postChangeSubscription({
        ...payload
      })
    }
  }

  const process3DSecure = (actionTokenId: string, retryCallback: () => void) => {
    // 3DS only fires after tokenization succeeded, which requires recurly to be ready — so the
    // instance is guaranteed present here (matching the original window.recurly.Risk() assumption).
    const risk = recurlyInstance.value!.Risk()
    const threeDSecure = risk.ThreeDSecure({ actionTokenId })
    const { trigger: triggerModal } = useModalStore()

    threeDSecure.on('error', () => {
      clearErrors()

      const error = {
        title: 'Unable to process your transaction.',
        message: 'Please try again or use a different payment method.'
      }
      const data = {
        error
      }
      triggerModal(data)
    })

    threeDSecure.on('token', (token: TokenPayload) => {
      // send `token.id` to your server to retry your API request
      set3DSToken(token.id)

      if (retryCallback) {
        retryCallback()
      }
    })

    return threeDSecure
  }

  return {
    loadRecurly,
    initRecurly,
    isRecurlyReady,
    instantiateElements,
    getTokenCC,
    getTokenPaypal,
    getToken,
    handleTransactionError,
    isBillingInfoUpdated,
    onBillingInfoUpdate,
    onChangeSubscription,
    onCreateNoPaySubscription,
    onCreateSubscription,
    recurlyElements,
    submitChangeSubscription,
    submitCreateSubscription,
    submitSubscriptionForm,
    paymentMethod,
    process3DSecure,
    setPaymentMethod,
    token,
    cashAppInstance,
    cashAppIsLoading,
    initCashApp,
    destroyCashApp,
    refreshCashApp,
    registerCashAppRedirectListener,
    availablePaymentMethods
  }
})
